import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLazyPopularviewsepQuery, useAddToCartMutation, useLazyCartViewQuery } from "../../redux/Api/api";
import { toast } from "react-toastify";
import ReactImageMagnify from "react-image-magnify";
import { useDispatch } from "react-redux";
import { saveCartCountHandler } from "../../redux/reducer/CartCountSlice";

const ProductViewpage = () => {
    const location = useLocation();
    const routeData = location?.state;
    const [product, setProduct] = useState(null);
    const [selectedImage, setSelectedImage] = useState("");
    const [quantity, setQuantity] = useState(1);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const user = JSON.parse(localStorage.getItem("user")) || null;
    const [showAddToCartPopup, setShowAddToCartPopup] = useState(false);
    const [showSuccessPopup, setShowSuccessPopup] = useState(false);
    const [addedProduct, setAddedProduct] = useState(null);

    const [getviewproductsep] = useLazyPopularviewsepQuery();
    const [addToCart] = useAddToCartMutation();
    const [cartlist] = useLazyCartViewQuery();

    useEffect(() => {
        if (routeData?._id) {
            getviewproductsep(routeData._id)
                .unwrap()
                .then((res) => {
                    if (res?.product) {
                        setProduct(res.product);
                        const mainImage = res.product.image || "https://via.placeholder.com/400";
                        setSelectedImage(mainImage);
                    } else {
                        setError("Product details not available");
                    }
                })
                .catch(() => setError("Error fetching product details"));
        }
    }, [routeData, getviewproductsep]);

    const getcartlist = () => {
        if (user?._id) {
            cartlist(user._id)
                .unwrap()
                .then(res => {
                    dispatch(saveCartCountHandler(res?.length));
                })
                .catch(err => {
                    console.error('Error fetching cart:', err);
                });
        }
    };

    const handleQuantityChange = (newQuantity) => {
        if (newQuantity > 0) {
            setQuantity(newQuantity);
        }
    };

    const handleClosePopup = () => {
        setShowAddToCartPopup(false);
    };

    const handleCloseSuccessPopup = () => {
        setShowSuccessPopup(false);
    };

    const handleConfirmAddToCart = () => {
        if (!user?._id) {
            toast.error("Please log in first.");
            return;
        }

        addToCart({ productId: product._id, userId: user._id, quantity: quantity })
            .unwrap()
            .then(() => {
                toast.success("Product added to cart successfully");
                getcartlist();
                setShowAddToCartPopup(false);
                setShowSuccessPopup(true);
            })
            .catch((err) => {
                console.error("Error adding to cart:", err);
                toast.error("Failed to add to cart.");
            });
    };

    const handleAddToCart = () => {
        if (!user?._id) {
            toast.error("Please log in first.");
            return;
        }

        setAddedProduct(product);
        setShowAddToCartPopup(true);
    };

    const handleBuyNow = () => {
        if (!user?._id) {
            toast.error("Please log in first.");
            return;
        }
        navigate("/checkout", {
            state: {
                buyNowProduct: {
                    product: product,
                    quantity: quantity
                }
            }
        });
    };

    const handleShareNow = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: product?.name,
                    text: `Check out this product: ${product?.name} for ₹${product?.price}!`,
                    url: window.location.href,
                });
                toast.success("Product shared successfully!");
            } catch {
                toast.error("Error sharing product");
            }
        } else {
            toast.error("Sharing not supported on this device.");
        }
    };

    if (!product) {
        return <div className="text-center mt-10 text-lg">Loading product details...</div>;
    }

    const productImages = product?.images || [];
    const imagesToShow = [selectedImage, ...productImages.filter(img => img !== selectedImage)];

    return (
        <div className="container mx-auto p-4 md:p-6 lg:p-10 mt-40">
            <div className="bg-white shadow-lg rounded-lg p-4 md:p-6 lg:p-8 flex flex-col md:flex-row">
                <div className="w-full md:w-1/2 flex flex-col items-center p-4">
                    <ReactImageMagnify
                        {...{
                            smallImage: {
                                alt: product?.name || "Product Image",
                                isFluidWidth: true,
                                src: selectedImage,
                            },
                            largeImage: {
                                src: selectedImage,
                                width: 1200,
                                height: 1200,
                            },
                            enlargedImageContainerStyle: { zIndex: 9 }
                        }}
                    />

                    <div className="mt-10 flex gap-2">
                        {imagesToShow.map((img, index) => (
                            <img
                                key={index}
                                src={img}
                                alt={`Thumbnail ${index + 1}`}
                                className={`w-16 h-16 cursor-pointer border rounded-md hover:border-gray-500 ${selectedImage === img ? 'border-2 border-blue-500' : ''}`}
                                onClick={() => setSelectedImage(img)}
                            />
                        ))}
                    </div>
                </div>

                <div className="w-full md:w-1/2 p-4">
                    <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold">{product?.name}</h1>
                    <p className="text-lg md:text-xl text-orange-500 font-bold mt-2">₹{product?.price}</p>
                    <p className="text-md md:text-lg text-gray-600 mt-2">Size: {product?.size}</p>
                    <div className="mt-4 flex items-center">
                        <label htmlFor="quantity" className="mr-2 text-gray-700 font-semibold">Quantity:</label>
                        <input
                            type="number"
                            id="quantity"
                            value={quantity}
                            min="1"
                            onChange={(e) => setQuantity(parseInt(e.target.value, 1))}
                            className="border border-gray-300 rounded px-3 py-2 w-16 text-center"
                        />
                    </div>
                    <div className="mt-6 flex flex-col sm:flex-row gap-4">
                        <button
                            className="w-full sm:w-auto bg-yellow-500 text-white font-semibold py-3 px-6 rounded-lg hover:bg-yellow-600 transition"
                            onClick={handleBuyNow}
                        >
                            Buy Now
                        </button>
                        <button 
                            className="w-full sm:w-auto bg-blue-600 text-white font-semibold py-3 px-6 rounded-lg hover:bg-blue-700 transition" 
                            onClick={handleAddToCart}
                        >
                            Add to Cart
                        </button>
                        <button 
                            className="w-full sm:w-auto bg-green-600 text-white font-semibold py-3 px-6 rounded-lg hover:bg-green-700 transition" 
                            onClick={handleShareNow}
                        >
                            Share Now
                        </button>
                    </div>
                    <div className="mt-6">
                        <h2 className="text-xl md:text-2xl font-semibold">Description</h2>
                        <p className="text-gray-700 text-md md:text-lg">{product?.description}</p>
                    </div>
                </div>
            </div>

            {/* Add to Cart Popup */}
            {showAddToCartPopup && product && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="bg-white p-10 shadow-lg w-[700px] min-h-[330px] flex relative rounded-lg">
                        <button
                            className="absolute top-4 right-4 text-gray-600 hover:text-gray-900 text-2xl"
                            onClick={handleClosePopup}
                        >
                            <i className="fas fa-times"></i>
                        </button>
                        <div className="w-1/3 flex justify-center items-center">
                            <img
                                src={selectedImage}
                                alt={product.name}
                                className="w-64 h-64 rounded-lg border"
                            />
                        </div>
                        <div className="w-2/3 pl-20 flex flex-col justify-between">
                            <p className="text-3xl font-bold text-gray-900 mt-2 pr-96">{product.name}</p>
                            <div className="grid grid-cols-2 gap-2 items-center">
                                <p className="text-lg font-medium text-gray-700 text-left">Price:</p>
                                <p className="text-xl font-semibold text-green-600 pr-52">₹{product.price}</p>
                                <p className="text-lg font-medium text-gray-700 text-left">Quantity:</p>
                                <div className="flex items-center border rounded-lg w-fit">
                                    <button
                                        className="bg-gray-200 px-4 py-1 rounded-l text-base"
                                        onClick={() => handleQuantityChange(quantity - 1)}
                                    >
                                        -
                                    </button>
                                    <p className="px-3 text-lg">{quantity}</p>
                                    <button
                                        className="bg-gray-200 px-4 py-1 rounded-r text-base"
                                        onClick={() => handleQuantityChange(quantity + 1)}
                                    >
                                        +
                                    </button>
                                </div>
                                <p className="text-lg font-medium text-gray-700 text-left">Total Price:</p>
                                <p className="text-2xl font-semibold text-green-600 pr-56">
                                    ₹{product.price * quantity}
                                </p>
                            </div>
                            <div className="flex mt-4">
                                <button
                                    className="bg-blue-600 text-white px-4 py-2 text-lg font-semibold w-1/2 hover:bg-blue-700 flex items-center justify-center gap-2 rounded"
                                    onClick={handleConfirmAddToCart}
                                >
                                    <i className="fas fa-shopping-cart"></i> Add to Cart
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Success Popup */}
            {showSuccessPopup && product && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="bg-white p-6 shadow-lg w-[600px] min-h-[350px] flex relative rounded-lg">
                        <button
                            className="absolute top-4 right-4 text-gray-600 hover:text-gray-900 text-2xl"
                            onClick={handleCloseSuccessPopup}
                        >
                            <i className="fas fa-times"></i>
                        </button>
                        <div className="w-full flex flex-col items-center text-center p-4">
                            <i className="fas fa-shopping-cart text-green-500 text-3xl"></i>
                            <p className="text-xl font-bold text-green-600 mt-2">Item added to your cart!</p>
                            <div className="flex items-center gap-4 mt-4">
                                <img
                                    src={selectedImage}
                                    alt={product.name}
                                    className="w-20 h-20 rounded-lg border"
                                />
                                <div className="text-left flex items-center gap-2">
                                    <label className="text-lg font-medium text-gray-700">Price:</label>
                                    <p className="text-xl font-semibold text-green-600">₹{product.price}</p>
                                </div>
                            </div>

                            <p className="text-lg font-medium text-gray-700 mt-4">Frequently Bought Together</p>
                            <div className="flex gap-4 mt-6 w-full">
                                <button
                                    className="bg-teal-600 text-white px-4 py-2 text-lg font-semibold flex-grow hover:bg-teal-700"
                                    onClick={handleCloseSuccessPopup}
                                >
                                    Back to Shopping
                                </button>
                                <button
                                    className="bg-blue-600 text-white px-4 py-2 text-lg font-semibold flex-grow hover:bg-blue-700"
                                    onClick={() => navigate('/cart')}
                                >
                                    Proceed to Checkout
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProductViewpage;